import { clampRect, toPixelRect } from './geometry'
import type { NormalizedRect } from '@/domain/types'

export interface OcrTextImageEnhancement {
  /** Fixed scale, or the maximum scale when targetContentHeight is set. */
  readonly scale: number
  readonly targetContentHeight?: number
  readonly minimumScale?: number
  readonly scaleStep?: number
  readonly padding: number
  readonly contrast: number
  readonly sharpen: number
  readonly interpolation?: 'smooth' | 'pixelated'
  readonly grayscale?: boolean
}

export interface OcrSourcePadding {
  readonly left?: number
  readonly right?: number
  readonly top?: number
  readonly bottom?: number
}

export interface PreparedOcrImage {
  readonly sourceRect: NormalizedRect
  readonly source: ImageData
  readonly processed: ImageData
}

export function expandOcrSourceRect(rect: NormalizedRect, padding: OcrSourcePadding): NormalizedRect {
  const left = Math.max(0, padding.left ?? 0)
  const right = Math.max(0, padding.right ?? 0)
  const top = Math.max(0, padding.top ?? 0)
  const bottom = Math.max(0, padding.bottom ?? 0)
  return clampRect({
    x: rect.x - rect.width * left,
    y: rect.y - rect.height * top,
    width: rect.width * (1 + left + right),
    height: rect.height * (1 + top + bottom),
  })
}

export function prepareOcrImage(
  source: CanvasImageSource | ImageData,
  width: number,
  height: number,
  rect: NormalizedRect,
  sourcePadding?: OcrSourcePadding,
  enhancement?: OcrTextImageEnhancement,
): PreparedOcrImage {
  const sourceRect = sourcePadding ? expandOcrSourceRect(rect, sourcePadding) : rect
  const sourceImage = drawSourceToImageData(source, width, height, sourceRect)
  return {
    sourceRect,
    source: sourceImage,
    processed: enhancement ? enhanceOcrTextImage(sourceImage, enhancement) : sourceImage,
  }
}

export function drawSourceToImageData(
  source: CanvasImageSource | ImageData,
  width: number,
  height: number,
  rect?: NormalizedRect,
): ImageData {
  if (source instanceof ImageData && !rect) return new ImageData(new Uint8ClampedArray(source.data), source.width, source.height)
  const canvas = new OffscreenCanvas(rect ? Math.max(1, Math.round(rect.width * width)) : width, rect ? Math.max(1, Math.round(rect.height * height)) : height)
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('2D canvas is unavailable')
  const drawable = source instanceof ImageData
    ? (() => {
        const fullCanvas = new OffscreenCanvas(source.width, source.height)
        const fullContext = fullCanvas.getContext('2d')
        if (!fullContext) throw new Error('2D canvas is unavailable')
        fullContext.putImageData(source, 0, 0)
        return fullCanvas
      })()
    : source
  if (rect) {
    const pixel = toPixelRect(rect, width, height)
    context.drawImage(drawable, pixel.x, pixel.y, pixel.width, pixel.height, 0, 0, canvas.width, canvas.height)
  } else {
    context.drawImage(drawable, 0, 0, width, height)
  }
  return context.getImageData(0, 0, canvas.width, canvas.height)
}

export function enhanceOcrTextImage(image: ImageData, options: OcrTextImageEnhancement): ImageData {
  const scale = resolveOcrTextScale(image.height, options)
  const padding = Math.max(0, Math.round(options.padding))
  const scaledPadding = Math.round(padding * scale)
  const contentWidth = Math.max(1, Math.round(image.width * scale))
  const contentHeight = Math.max(1, Math.round(image.height * scale))
  const scaled = options.interpolation === 'pixelated'
    ? scalePixelatedWithPadding(image, contentWidth, contentHeight, scaledPadding)
    : scaleSmoothWithPadding(image, contentWidth, contentHeight, scaledPadding)
  const prepared = options.grayscale ? applyGrayscale(scaled) : scaled
  return applyTextContrastAndSharpen(prepared, options.contrast, options.sharpen)
}

export function resolveOcrTextScale(sourceHeight: number, options: OcrTextImageEnhancement): number {
  const maximumScale = Math.max(1, options.scale)
  const targetHeight = options.targetContentHeight
  if (typeof targetHeight !== 'number' || !Number.isFinite(targetHeight) || targetHeight <= 0
    || !Number.isFinite(sourceHeight) || sourceHeight <= 0) {
    return maximumScale
  }

  const minimumScale = Math.min(maximumScale, Math.max(1, options.minimumScale ?? 1))
  const configuredScaleStep = options.scaleStep
  const scaleStep = typeof configuredScaleStep === 'number' && Number.isFinite(configuredScaleStep) && configuredScaleStep > 0
    ? configuredScaleStep
    : 0.25
  const desiredScale = targetHeight / sourceHeight
  const steppedScale = Math.round(desiredScale / scaleStep) * scaleStep
  const resolved = Math.min(maximumScale, Math.max(minimumScale, steppedScale))
  return Math.round(resolved * 10_000) / 10_000
}

function scaleSmoothWithPadding(
  image: ImageData,
  contentWidth: number,
  contentHeight: number,
  scaledPadding: number,
): ImageData {
  const sourceCanvas = new OffscreenCanvas(image.width, image.height)
  const sourceContext = sourceCanvas.getContext('2d')
  if (!sourceContext) throw new Error('2D canvas is unavailable')
  sourceContext.putImageData(image, 0, 0)

  const canvas = new OffscreenCanvas(contentWidth + scaledPadding * 2, contentHeight + scaledPadding * 2)
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('2D canvas is unavailable')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'

  drawReplicatedPadding(context, sourceCanvas, image.width, image.height, contentWidth, contentHeight, scaledPadding)
  return context.getImageData(0, 0, canvas.width, canvas.height)
}

function scalePixelatedWithPadding(
  image: ImageData,
  contentWidth: number,
  contentHeight: number,
  scaledPadding: number,
): ImageData {
  const outputWidth = contentWidth + scaledPadding * 2
  const outputHeight = contentHeight + scaledPadding * 2
  const output = new Uint8ClampedArray(outputWidth * outputHeight * 4)

  for (let y = 0; y < outputHeight; y += 1) {
    const sourceY = Math.min(image.height - 1, Math.max(0, Math.floor((y - scaledPadding) * image.height / contentHeight)))
    for (let x = 0; x < outputWidth; x += 1) {
      const sourceX = Math.min(image.width - 1, Math.max(0, Math.floor((x - scaledPadding) * image.width / contentWidth)))
      const sourceIndex = (sourceY * image.width + sourceX) * 4
      const outputIndex = (y * outputWidth + x) * 4
      output[outputIndex] = image.data[sourceIndex] ?? 0
      output[outputIndex + 1] = image.data[sourceIndex + 1] ?? 0
      output[outputIndex + 2] = image.data[sourceIndex + 2] ?? 0
      output[outputIndex + 3] = image.data[sourceIndex + 3] ?? 255
    }
  }

  return new ImageData(output, outputWidth, outputHeight, { colorSpace: image.colorSpace })
}

export function applyGrayscale(image: ImageData): ImageData {
  const output = new Uint8ClampedArray(image.data)
  for (let index = 0; index < output.length; index += 4) {
    const luminance = Math.round(
      (output[index] ?? 0) * 0.299
      + (output[index + 1] ?? 0) * 0.587
      + (output[index + 2] ?? 0) * 0.114,
    )
    output[index] = luminance
    output[index + 1] = luminance
    output[index + 2] = luminance
  }
  return new ImageData(output, image.width, image.height, { colorSpace: image.colorSpace })
}

function drawReplicatedPadding(
  context: OffscreenCanvasRenderingContext2D,
  source: OffscreenCanvas,
  sourceWidth: number,
  sourceHeight: number,
  contentWidth: number,
  contentHeight: number,
  padding: number,
): void {
  context.drawImage(source, 0, 0, sourceWidth, sourceHeight, padding, padding, contentWidth, contentHeight)
  if (!padding) return

  context.drawImage(source, 0, 0, sourceWidth, 1, padding, 0, contentWidth, padding)
  context.drawImage(source, 0, sourceHeight - 1, sourceWidth, 1, padding, padding + contentHeight, contentWidth, padding)
  context.drawImage(source, 0, 0, 1, sourceHeight, 0, padding, padding, contentHeight)
  context.drawImage(source, sourceWidth - 1, 0, 1, sourceHeight, padding + contentWidth, padding, padding, contentHeight)

  context.drawImage(source, 0, 0, 1, 1, 0, 0, padding, padding)
  context.drawImage(source, sourceWidth - 1, 0, 1, 1, padding + contentWidth, 0, padding, padding)
  context.drawImage(source, 0, sourceHeight - 1, 1, 1, 0, padding + contentHeight, padding, padding)
  context.drawImage(source, sourceWidth - 1, sourceHeight - 1, 1, 1, padding + contentWidth, padding + contentHeight, padding, padding)
}

export function applyTextContrastAndSharpen(image: ImageData, contrast: number, sharpen: number): ImageData {
  const source = image.data
  const output = new Uint8ClampedArray(source)
  const safeContrast = Math.max(0, contrast)
  const safeSharpen = Math.max(0, sharpen)
  const rowStride = image.width * 4

  for (let y = 1; y < image.height - 1; y += 1) {
    for (let x = 1; x < image.width - 1; x += 1) {
      const index = y * rowStride + x * 4
      for (let channel = 0; channel < 3; channel += 1) {
        const center = source[index + channel] ?? 0
        const neighborAverage = (
          (source[index - 4 + channel] ?? center)
          + (source[index + 4 + channel] ?? center)
          + (source[index - rowStride + channel] ?? center)
          + (source[index + rowStride + channel] ?? center)
        ) / 4
        const contrasted = 128 + (center - 128) * safeContrast
        output[index + channel] = contrasted + (center - neighborAverage) * safeSharpen
      }
    }
  }

  return new ImageData(output, image.width, image.height, { colorSpace: image.colorSpace })
}

export function imageChanged(previous: ImageData | null, current: ImageData, threshold: number): boolean {
  if (!previous || previous.width !== current.width || previous.height !== current.height) return true
  const pixelCount = current.width * current.height
  const step = Math.max(1, Math.floor(Math.sqrt(pixelCount / 2_048)))
  let difference = 0
  let changedPixels = 0
  let sampled = 0
  for (let y = 0; y < current.height; y += step) {
    for (let x = 0; x < current.width; x += step) {
      const index = (y * current.width + x) * 4
      const pixelDifference = (
        Math.abs((current.data[index] ?? 0) - (previous.data[index] ?? 0))
        + Math.abs((current.data[index + 1] ?? 0) - (previous.data[index + 1] ?? 0))
        + Math.abs((current.data[index + 2] ?? 0) - (previous.data[index + 2] ?? 0))
      ) / (255 * 3)
      difference += pixelDifference
      if (pixelDifference >= 0.08) changedPixels += 1
      sampled += 1
    }
  }
  if (!sampled) return false
  const localizedChangeThreshold = Math.min(0.006, Math.max(0.0015, threshold * 0.25))
  return difference / sampled >= threshold || changedPixels / sampled >= localizedChangeThreshold
}

export function imageInferenceRequired(
  previous: ImageData | null,
  current: ImageData,
  lastInferredAt: number | null,
  capturedAt: number,
  maxIntervalMs: number,
  changeThreshold: number,
): boolean {
  return previous === null
    || lastInferredAt === null
    || capturedAt - lastInferredAt >= maxIntervalMs
    || imageChanged(previous, current, changeThreshold)
}
